import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { architectAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, isBefore } from 'date-fns';
import {
  AlertTriangle, Calendar, CheckCircle2, ChevronRight, ChevronLeft, Users,
  Building, BarChart3, ClipboardList, FileText, X
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LabelList, CartesianGrid } from 'recharts';

// Presentation-only tone map (approved reference palette).
const TONE = {
  green: 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-300',
  amber: 'bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-300',
  red: 'bg-red-50 border-red-100 text-red-600 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300',
  blue: 'bg-blue-50 border-blue-100 text-blue-700 dark:bg-blue-950/30 dark:border-blue-900 dark:text-blue-300',
  violet: 'bg-violet-50 border-violet-100 text-violet-700 dark:bg-violet-950/30 dark:border-violet-900 dark:text-violet-300',
};
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning!' : h < 17 ? 'Good afternoon!' : 'Good evening!';
};

// Every number comes from the existing /architect/dashboard + /architect/team + /architect/dashboard/list
// responses. No new endpoint, no hard-coded value, no financial field.
const ATTENTION_SOURCES = [
  { card: 'overdue', issue: 'Overdue deliverable' },
  { card: 'revision_required', issue: 'Revision required' },
  { card: 'awaiting_client_approval', issue: 'Awaiting client approval' },
  { card: 'handover_pending', issue: 'Handover pending' },
];

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const PriorityTag = ({ value }) => {
  const p = String(value || '').toLowerCase();
  const cls = p === 'urgent' ? TONE.red : p === 'high' ? TONE.amber : p === 'medium' ? TONE.amber : TONE.blue;
  return <span className={`inline-flex whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] font-semibold ${cls}`}>{p ? p.charAt(0).toUpperCase() + p.slice(1) : 'Not set'}</span>;
};

export const ArchitectManagerDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [team, setTeam] = useState([]);
  const [dueTasks, setDueTasks] = useState([]);
  const [attention, setAttention] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [drill, setDrill] = useState(null);
  const [showTeam, setShowTeam] = useState(false);
  const [allAttention, setAllAttention] = useState(false);
  // Exactly one KPI tile can be visually active at a time.
  const [activeTile, setActiveTile] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  const [events, setEvents] = useState([]);
  const [calDate, setCalDate] = useState(new Date());
  const [calTab, setCalTab] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const safeList = (card) => architectAPI.dashboardList(card).catch(() => ({ data: { items: [] } }));
      const [dashRes, teamRes, tasksRes, dueSoonRes, inProgressRes, ...attnRes] = await Promise.all([
        architectAPI.dashboard(),
        architectAPI.team().catch(() => ({ data: [] })),
        safeList('team_open_tasks'),
        safeList('due_soon'),
        safeList('deliverables_in_progress'),
        ...ATTENTION_SOURCES.map((s) => safeList(s.card)),
      ]);
      setDash(dashRes.data);
      setTeam(teamRes.data || []);
      // Full list is kept intact; the dashboard just shows the top few. Overdue first (earliest due
      // date), then nearest due date, tasks without a due date last.
      setDueTasks((tasksRes.data.items || []).slice().sort((a, b) => {
        const av = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const bv = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        return av - bv;
      }));
      // Architect calendar events — built from the SAME existing card responses already fetched
      // (deliverable due dates + team task due dates). No new endpoint, no invented events.
      const evts = [];
      const pushDeliverables = (items) => (items || []).forEach((d) => {
        if (d.due_date) evts.push({ id: `d-${d.id}`, kind: 'deliverable', title: d.name, status: d.status,
          project_name: d.project_name, lead_id: d.lead_id, date: d.due_date });
      });
      pushDeliverables(dueSoonRes.data.items);
      pushDeliverables(inProgressRes.data.items);
      (tasksRes.data.items || []).forEach((t) => {
        if (t.due_date) evts.push({ id: `t-${t.id}`, kind: 'task', title: t.title, status: t.status,
          project_name: t.project_name, lead_id: t.lead_id, date: t.due_date, assigned_to: t.assigned_to });
      });
      attnRes.forEach((res, i) => {
        if (ATTENTION_SOURCES[i].card === 'overdue') pushDeliverables(res.data.items);
      });
      const seen = new Set();
      setEvents(evts.filter((e) => (seen.has(e.id) ? false : seen.add(e.id))));

      const byProject = new Map();
      attnRes.forEach((res, i) => {
        (res.data.items || []).forEach((it) => {
          const leadId = it.lead_id || it.id;
          if (!leadId) return;
          const row = byProject.get(leadId) || {
            lead_id: leadId,
            project: it.project_name || it.customer_name || 'Untitled project',
            issues: [], days: null, stage: null,
          };
          if (!row.issues.includes(ATTENTION_SOURCES[i].issue)) row.issues.push(ATTENTION_SOURCES[i].issue);
          const days = it.overdue_days ?? null;
          if (days != null && (row.days == null || days > row.days)) row.days = days;
          row.stage = row.stage || it.status || it.review_status || it.client_status || it.architect_lifecycle || null;
          byProject.set(leadId, row);
        });
      });
      setAttention([...byProject.values()].sort((a, b) => (b.days || 0) - (a.days || 0)));
    } catch (e) {
      const msg = e.response?.status === 403
        ? 'Your role does not have access to the Architect Manager workspace.'
        : (e.response?.data?.detail || 'Failed to load manager dashboard');
      setLoadError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCard = async (key, label) => {
    setActiveTile(key);
    setShowTeam(false);
    setDrill({ card: key, label, loading: true, items: [] });
    try {
      const r = await architectAPI.dashboardList(key);
      setDrill({ card: key, label, loading: false, ...r.data });
    } catch (e) { setDrill(null); setActiveTile(null); toast.error(e.response?.data?.detail || 'Could not open list'); }
  };

  const openTeamPanel = (key) => {
    setDrill(null);
    setActiveTile((prev) => (prev === key && showTeam ? null : key));
    setShowTeam((prev) => !(prev && activeTile === key));
  };

  const nameOf = useMemo(() => {
    const map = {};
    team.forEach((t) => { map[t.id] = t.name || t.user_id; });
    return (id) => map[id] || 'Unassigned';
  }, [team]);

  const cards = dash?.cards || {};
  const mgr = dash?.manager_cards || {};
  const value = (key) => cards[key] ?? mgr[key] ?? 0;
  const activeMembers = team.filter((t) => (t.active_projects || 0) > 0).length;

  const groups = [
    { id: 'team-members', title: 'Team Members', subtitle: 'Architect team overview', icon: Users, tone: 'blue', items: [
      { key: 'team_total', label: 'Total', tone: 'blue', count: team.length, onClick: () => openTeamPanel('team_total') },
      { key: 'team_active', label: 'Active (with live work)', tone: 'green', count: activeMembers, onClick: () => openTeamPanel('team_active') },
    ] },
    { id: 'team-projects', title: 'Projects (Team)', subtitle: 'Deliverable counts across all architects', icon: Building, tone: 'amber', items: [
      { key: 'active_projects', label: 'Active', tone: 'blue' },
      { key: 'due_soon', label: 'Deliverables Due Soon', tone: 'amber' },
      { key: 'overdue', label: 'Overdue Deliverables', tone: 'red' },
    ] },
    { id: 'team-tasks', title: 'Team Tasks', subtitle: 'All architect tasks', icon: ClipboardList, tone: 'violet', items: [
      { key: 'team_tasks_due_today', label: 'Due Today', tone: 'blue' },
      { key: 'team_open_tasks', label: 'Pending', tone: 'violet' },
      { key: 'team_overdue_tasks', label: 'Overdue Tasks', tone: 'red' },
    ] },
    { id: 'approvals-revisions', title: 'Approvals & Revisions', subtitle: 'Internal and client approvals', icon: CheckCircle2, tone: 'green', items: [
      { key: 'awaiting_internal_review', label: 'Internal Review', tone: 'blue' },
      { key: 'awaiting_client_approval', label: 'Client Approval', tone: 'amber' },
      { key: 'client_changes_requested', label: 'Changes Requested', tone: 'red' },
    ] },
  ];

  const chartData = team.map((t) => ({
    // Display-only: the logged-in manager is shown as "Me". No user record / id / ownership change.
    name: t.id === user?.id ? 'Me' : (t.name || t.user_id || ''),
    Assigned: t.assigned_projects || 0,
    Completed: t.completed_this_month || 0,
    Pending: t.pending_tasks || 0,
    Overdue: t.overdue || 0,
  }));

  // Calendar derivations — same helpers/pattern as the Sales Calendar page.
  const calDays = useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(calDate)),
    end: endOfWeek(endOfMonth(calDate)),
  }), [calDate]);

  const eventStyle = (ev) => {
    const d = new Date(ev.date);
    if (isSameDay(d, new Date())) return { bg: 'bg-yellow-500', label: 'Today' };
    if (isBefore(d, new Date())) return { bg: 'bg-red-500', label: 'Overdue' };
    return { bg: 'bg-blue-500', label: 'Upcoming' };
  };

  const eventsForDay = (day) => events.filter((e) => isSameDay(new Date(e.date), day));

  const listedEvents = useMemo(() => {
    const byDate = [...events].sort((a, b) => new Date(a.date) - new Date(b.date));
    if (activeDay) return byDate.filter((e) => new Date(e.date).toDateString() === activeDay);
    if (calTab === 'today') return byDate.filter((e) => isSameDay(new Date(e.date), new Date()));
    if (calTab === 'overdue') return byDate.filter((e) => isBefore(new Date(e.date), new Date()) && !isSameDay(new Date(e.date), new Date()));
    return byDate;
  }, [events, activeDay, calTab]);

  return (
    <div className="space-y-4" data-testid="architect-manager-container">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-outfit font-semibold">Architect Manager Workspace</h1>
          <p className="text-sm text-muted-foreground">Team performance, projects, tasks and approvals at a glance. Financial data is not shown.</p>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <Calendar className="h-5 w-5 text-primary" />
          <div className="leading-tight">
            <p className="text-sm font-semibold">{new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}</p>
            <p className="text-xs text-muted-foreground">{greeting()}</p>
          </div>
        </div>
      </div>

      {loading && !dash ? (
        <div className="flex h-40 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary" /></div>
      ) : loadError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm dark:border-red-900 dark:bg-red-950/30" data-testid="mgr-access-error">
          <p className="font-semibold text-red-700 dark:text-red-300">This workspace could not be loaded</p>
          <p className="mt-1 text-red-700/80 dark:text-red-300/80">{loadError}</p>
          <button onClick={load} data-testid="mgr-retry" className="mt-3 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 dark:border-red-800 dark:text-red-300">Try again</button>
        </div>
      ) : (
      <>
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="manager-dashboard-cards">
          {groups.map((group) => (
            <div key={group.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid={`mgr-group-${group.id}`}>
              <div className="mb-3 flex items-center gap-3">
                <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border ${TONE[group.tone]}`}>
                  <group.icon className="h-4.5 w-4.5" />
                </span>
                <div className="min-w-0 flex-1 leading-tight">
                  <p className="truncate text-base font-semibold">{group.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{group.subtitle}</p>
                </div>
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              </div>
              <div className={`grid gap-2 ${group.items.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {group.items.map((item) => (
                  <button key={item.key} data-testid={`mgr-card-${item.key}`}
                    onClick={item.onClick || (() => openCard(item.key, item.label))}
                    className={`rounded-lg border px-2 py-2 text-left transition-all hover:-translate-y-0.5 ${TONE[item.tone]} ${
                      activeTile === item.key ? 'ring-2 ring-primary/50' : ''}`}>
                    <p className="text-[10px] font-medium leading-tight opacity-80">{item.label}</p>
                    <p className="mt-1 text-2xl font-outfit font-semibold leading-none">{item.count ?? value(item.key)}</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {showTeam && (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid="mgr-team-workload">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-base font-semibold">Architect workload</p>
              <button data-testid="mgr-team-workload-close" onClick={() => { setShowTeam(false); setActiveTile(null); }} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
            </div>
            <table className="w-full min-w-[640px] text-[13px]">
              <thead><tr className="bg-slate-50 text-left text-xs text-muted-foreground dark:bg-slate-800/60">
                <th className="rounded-l-md py-2 pl-3">Architect</th><th>Assigned</th><th>Active</th><th>Pending Tasks</th>
                <th>Due Today</th><th>Overdue</th><th>Revision</th><th className="rounded-r-md pr-3">Completed (month)</th>
              </tr></thead>
              <tbody>
                {team.map((t, i) => (
                  <tr key={t.id} className="border-b border-slate-100 last:border-0 dark:border-slate-800" data-testid={`mgr-team-row-${i}`}>
                    <td className="py-2 pl-3 font-medium">{t.id === user?.id ? 'Me' : (t.name || t.user_id)}</td>
                    <td>{t.assigned_projects}</td><td>{t.active_projects}</td><td>{t.pending_tasks}</td>
                    <td>{t.due_today}</td><td className={t.overdue ? 'font-semibold text-red-600 dark:text-red-400' : ''}>{t.overdue}</td>
                    <td>{t.revision_pending}</td><td className="pr-3">{t.completed_this_month}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {drill && (
          <div className="clay-card p-4" data-testid="mgr-drilldown">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold" data-testid="mgr-drilldown-title">
                {drill.label} · <span data-testid="mgr-drilldown-count">{drill.loading ? '…' : drill.count}</span>
              </p>
              <button data-testid="mgr-drilldown-close" onClick={() => { setDrill(null); setActiveTile(null); }} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
            </div>
            {drill.loading ? <p className="text-sm text-muted-foreground">Loading…</p>
              : drill.count === 0 ? <p className="text-sm text-muted-foreground" data-testid="mgr-drilldown-empty">Nothing here right now.</p>
              : <ul className="divide-y text-sm">
                  {drill.items.slice(0, 40).map((it, i) => (
                    <li key={it.id || i} className="flex items-center justify-between py-2" data-testid={`mgr-drill-row-${i}`}>
                      <span>{it.name || it.title || it.deliverable || it.project_name || it.customer_name}
                        {it.project_name && (it.name || it.title || it.deliverable) ? <span className="text-muted-foreground"> · {it.project_name}</span> : null}</span>
                      <span className="flex items-center gap-2 text-xs text-muted-foreground">
                        {it.status && <span>{it.status}</span>}
                        {(it.lead_id || it.id) && (
                          <button className="rounded border px-2 py-0.5" data-testid={`mgr-drill-open-${i}`}
                            onClick={() => navigate(`/architect/projects/${it.lead_id || it.id}`)}>Open</button>)}
                      </span>
                    </li>))}
                </ul>}
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid="mgr-team-performance">
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <span className={`flex h-9 w-9 items-center justify-center rounded-lg border ${TONE.blue}`}>
              <BarChart3 className="h-4.5 w-4.5" />
            </span>
            <div className="min-w-0 flex-1 leading-tight">
              <p className="text-base font-semibold">Team Performance</p>
              <p className="text-xs text-muted-foreground">Assigned vs Completed vs Pending vs Overdue per architect</p>
            </div>
          </div>
          {chartData.length === 0 ? <p className="text-sm text-muted-foreground">No architects on the roster yet.</p> : (
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 16, right: 8, left: -18, bottom: 0 }} barCategoryGap="22%">
                  <CartesianGrid vertical={false} stroke="currentColor" className="text-slate-200 dark:text-slate-800" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} height={28} axisLine={false} tickLine={false} stroke="currentColor" className="text-muted-foreground" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={44} axisLine={false} tickLine={false} stroke="currentColor" className="text-muted-foreground" />
                  <Tooltip cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} height={20} verticalAlign="top" align="right" iconType="circle" iconSize={8} />
                  {[['Assigned', '#3b82f6'], ['Completed', '#10b981'], ['Pending', '#f59e0b'], ['Overdue', '#ef4444']].map(([k, c]) => (
                    <Bar key={k} dataKey={k} fill={c} radius={[3, 3, 0, 0]} maxBarSize={22}>
                      <LabelList dataKey={k} position="top" style={{ fontSize: 10, fontWeight: 600, fill: 'currentColor' }} />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid="mgr-team-due-tasks">
            <div className="mb-2 flex items-center gap-2">
              <ClipboardList className="h-4.5 w-4.5 text-primary" />
              <p className="text-base font-semibold">Team Due Tasks</p>
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">{Math.min(dueTasks.length, 8)}</span>
              {dueTasks.length > 8 && (
                <button data-testid="mgr-due-tasks-view-all" onClick={() => openCard('team_open_tasks', 'Pending')}
                  className="ml-auto text-xs font-medium text-primary hover:underline">View All</button>
              )}
            </div>
            {dueTasks.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="mgr-due-tasks-empty">No open team tasks.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px] text-[13px]">
                  <thead><tr className="bg-slate-50 text-left text-xs text-muted-foreground dark:bg-slate-800/60">
                    <th className="rounded-l-md py-2 pl-3">Task</th><th>Architect</th><th>Project / Client</th><th>Due Date</th><th>Priority</th><th className="rounded-r-md pr-3 text-center">Action</th>
                  </tr></thead>
                  <tbody>
                    {dueTasks.slice(0, 8).map((t, i) => {
                      const late = t.due_date && new Date(t.due_date) < new Date();
                      const p = String(t.priority || '').toLowerCase();
                      const bar = p === 'urgent' ? 'bg-red-500' : p === 'high' ? 'bg-orange-500' : p === 'medium' ? 'bg-amber-400' : 'bg-blue-400';
                      return (
                        <tr key={t.id || i} className="border-b border-slate-100 last:border-0 dark:border-slate-800" data-testid={`mgr-due-task-${i}`}>
                          <td className="py-2 pl-3">
                            <span className="flex items-start gap-2">
                              <span className={`mt-0.5 h-5 w-1 flex-shrink-0 rounded-full ${bar}`} />
                              <span>{t.title}</span>
                            </span>
                          </td>
                          <td>{nameOf(t.assigned_to)}</td>
                          <td className="text-muted-foreground">{t.project_name || '—'}</td>
                          <td className={`font-medium ${late ? 'text-red-600 dark:text-red-400' : ''}`}>{fmtDate(t.due_date)}</td>
                          <td><PriorityTag value={t.priority} /></td>
                          <td className="pr-3 text-center">
                            <button className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
                              data-testid={`mgr-due-task-open-${i}`}
                              onClick={() => navigate(`/architect/projects/${t.lead_id}`)}>Open</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid="mgr-attention">
            <div className="mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4.5 w-4.5 text-amber-500" />
              <p className="text-base font-semibold">Projects Requiring Attention</p>
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">{attention.length}</span>
              {attention.length > 8 && (
                <button data-testid="mgr-attention-view-all" onClick={() => setAllAttention((v) => !v)}
                  className="ml-auto text-xs font-medium text-primary hover:underline">{allAttention ? 'Show less' : 'View All'}</button>
              )}
            </div>
            {attention.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="mgr-attention-empty">No exceptions right now.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-[13px]">
                  <thead><tr className="bg-slate-50 text-left text-xs text-muted-foreground dark:bg-slate-800/60">
                    <th className="rounded-l-md py-2 pl-3">Project / Client</th><th>Issue</th><th className="whitespace-nowrap px-2">Days</th><th className="px-2">Stage</th><th className="rounded-r-md pr-3 text-center">Action</th>
                  </tr></thead>
                  <tbody>
                    {(allAttention ? attention : attention.slice(0, 8)).map((r, i) => (
                      <tr key={r.lead_id} className="border-b border-slate-100 last:border-0 dark:border-slate-800" data-testid={`mgr-attention-row-${i}`}>
                        <td className="py-2 pl-3">
                          <span className="flex items-center gap-2">
                            <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border ${TONE.amber}`}>
                              <Building className="h-3.5 w-3.5" />
                            </span>
                            <span className="font-medium">{r.project}</span>
                          </span>
                        </td>
                        <td className="text-red-600 dark:text-red-400">{r.issues.join(', ')}</td>
                        <td className={`px-2 ${r.days ? 'font-semibold text-red-600 dark:text-red-400' : ''}`}>{r.days != null ? r.days : '—'}</td>
                        <td className="px-2">
                          {r.stage ? <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-medium ${TONE.blue}`}>{r.stage}</span> : '—'}
                        </td>
                        <td className="pr-3 text-center">
                          <button className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
                            data-testid={`mgr-attention-open-${i}`}
                            onClick={() => navigate(`/architect/projects/${r.lead_id}`)}>Open</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Architecture Calendar — same pattern/design as the Sales Calendar page (date-fns month grid,
            prev/Today/next header, day cells with 2 event chips + "+N more", legend, side tab list,
            detail modal). Events come only from existing architect card data already fetched. */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4" data-testid="mgr-calendar">
          <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4.5 w-4.5 text-primary" />
                <h2 className="text-xl font-outfit font-medium" data-testid="mgr-calendar-month">{format(calDate, 'MMMM yyyy')}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="mgr-calendar-prev" onClick={() => setCalDate(subMonths(calDate, 1))} className="rounded-lg p-2 hover:bg-slate-100 dark:hover:bg-slate-800">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button data-testid="mgr-calendar-today" onClick={() => { setCalDate(new Date()); setActiveDay(new Date().toDateString()); }}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800">Today</button>
                <button data-testid="mgr-calendar-next" onClick={() => setCalDate(addMonths(calDate, 1))} className="rounded-lg p-2 hover:bg-slate-100 dark:hover:bg-slate-800">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mb-2 grid grid-cols-7 gap-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} className="py-2 text-center text-xs font-medium text-muted-foreground">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {calDays.map((day) => {
                const dayEvents = eventsForDay(day);
                const isCurrentMonth = isSameMonth(day, calDate);
                const isToday = isSameDay(day, new Date());
                return (
                  <div key={day.toISOString()} data-testid={`mgr-calendar-day-${format(day, 'yyyy-MM-dd')}`}
                    onClick={() => dayEvents.length && setActiveDay((prev) => (prev === day.toDateString() ? null : day.toDateString()))}
                    className={`min-h-[96px] rounded-lg border border-slate-200 p-2 dark:border-slate-700 ${
                      isCurrentMonth ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 opacity-50 dark:bg-slate-800/50'
                    } ${isToday ? 'ring-2 ring-primary' : ''} ${activeDay === day.toDateString() ? 'ring-2 ring-primary/50' : ''} ${dayEvents.length ? 'cursor-pointer' : ''}`}>
                    <p className={`mb-1 text-sm font-medium ${isToday ? 'text-primary' : ''}`}>{format(day, 'd')}</p>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 2).map((ev) => {
                        const s = eventStyle(ev);
                        return (
                          <div key={ev.id} data-testid={`mgr-calendar-event-${ev.id}`}
                            onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
                            className={`${s.bg} flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-xs text-white hover:opacity-90`}>
                            {ev.kind === 'task' ? <ClipboardList className="h-3 w-3 flex-shrink-0" /> : <FileText className="h-3 w-3 flex-shrink-0" />}
                            <span className="truncate">{ev.title}</span>
                          </div>
                        );
                      })}
                      {dayEvents.length > 2 && <p className="text-xs text-muted-foreground">+{dayEvents.length - 2} more</p>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              <div className="flex items-center gap-1"><div className="h-3 w-3 rounded bg-yellow-500" />Today</div>
              <div className="flex items-center gap-1"><div className="h-3 w-3 rounded bg-blue-500" />Upcoming</div>
              <div className="flex items-center gap-1"><div className="h-3 w-3 rounded bg-red-500" />Overdue</div>
              <div className="flex items-center gap-1"><FileText className="h-3 w-3" />Deliverable</div>
              <div className="flex items-center gap-1"><ClipboardList className="h-3 w-3" />Task</div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-3 flex gap-1 text-xs">
              {['all', 'today', 'overdue'].map((t) => (
                <button key={t} data-testid={`mgr-calendar-tab-${t}`} onClick={() => { setCalTab(t); setActiveDay(null); }}
                  className={`rounded px-2 py-1 capitalize ${calTab === t ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>{t}</button>
              ))}
            </div>
            {activeDay && (
              <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground" data-testid="mgr-calendar-day-filter">
                <span>{format(new Date(activeDay), 'MMM d, yyyy')}</span>
                <button data-testid="mgr-calendar-day-clear" onClick={() => setActiveDay(null)} className="text-primary hover:underline">Clear</button>
              </div>
            )}
            <div className="max-h-[420px] space-y-2 overflow-y-auto">
              {listedEvents.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground" data-testid="mgr-calendar-empty">No architecture deadlines</p>
              ) : listedEvents.slice(0, 30).map((ev, i) => {
                const s = eventStyle(ev);
                return (
                  <div key={ev.id} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700" data-testid={`mgr-calendar-item-${i}`}>
                    <div className="flex items-start gap-2">
                      <div className={`rounded p-1.5 text-white ${s.bg}`}>
                        {ev.kind === 'task' ? <ClipboardList className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <button onClick={() => navigate(`/architect/projects/${ev.lead_id}`)}
                          data-testid={`mgr-calendar-item-open-${i}`}
                          className="w-full truncate text-left text-sm font-medium hover:underline">{ev.title}</button>
                        <p className="truncate text-xs text-muted-foreground">{ev.project_name || '—'}</p>
                        <p className="text-xs text-muted-foreground">{format(new Date(ev.date), 'MMM d, yyyy')}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] text-white ${s.bg}`}>{s.label}</span>
                          <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] dark:bg-slate-700">{ev.kind === 'task' ? 'Task' : 'Deliverable'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {selectedEvent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedEvent(null)}>
            <div className="w-full max-w-md rounded-xl bg-white p-6 dark:bg-[#131A2B]" onClick={(e) => e.stopPropagation()} data-testid="mgr-calendar-detail">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-outfit font-semibold">{selectedEvent.kind === 'task' ? 'Task' : 'Deliverable'} Details</h2>
                <button data-testid="mgr-calendar-detail-close" onClick={() => setSelectedEvent(null)}><X className="h-5 w-5" /></button>
              </div>
              <div className="space-y-2 text-sm">
                <p><span className="text-muted-foreground">Title: </span>{selectedEvent.title}</p>
                <p><span className="text-muted-foreground">Project: </span>{selectedEvent.project_name || '—'}</p>
                <p><span className="text-muted-foreground">Due: </span>{format(new Date(selectedEvent.date), 'MMM d, yyyy')}</p>
                <p><span className="text-muted-foreground">Status: </span>{selectedEvent.status || '—'}</p>
                {selectedEvent.kind === 'task' && <p><span className="text-muted-foreground">Architect: </span>{nameOf(selectedEvent.assigned_to)}</p>}
              </div>
              <button data-testid="mgr-calendar-detail-open"
                onClick={() => { navigate(`/architect/projects/${selectedEvent.lead_id}`); setSelectedEvent(null); }}
                className="mt-4 w-full rounded-lg bg-primary px-3 py-2 text-sm text-white">Open Project Details</button>
            </div>
          </div>
        )}
      </>)}
    </div>
  );
};
export default ArchitectManagerDashboardPage;
